import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBranding, brandingFallback } from "@/lib/branding";
import { MailCheck, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function ForgotPassword() {
    const branding = useBranding();
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || submitting) return;
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
            setSent(true);
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Could not send reset email. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-[80vh] flex items-center justify-center px-6 py-12"
            data-testid="forgot-password-page"
        >
            <Card className="w-full max-w-md border-border">
                <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <img
                            src={branding.logo_url}
                            className="h-10 w-10 rounded-full object-contain"
                            alt="" onError={brandingFallback}
                        />
                        <div>
                            <div className="font-display text-primary text-xl">
                                Forgot password
                            </div>
                            <div className="text-xs text-muted-foreground uppercase tracking-widest">
                                {branding.institution_name}
                            </div>
                        </div>
                    </div>

                    {sent ? (
                        <div
                            className="space-y-4 text-sm text-foreground/85"
                            data-testid="forgot-password-sent"
                        >
                            <div className="h-12 w-12 rounded-full bg-accent/15 text-accent-foreground flex items-center justify-center mx-auto">
                                <MailCheck className="h-6 w-6 text-accent" />
                            </div>
                            <p className="text-center">
                                If <strong>{email}</strong> matches an active account, we&rsquo;ve
                                emailed a secure reset link. It expires in 60 minutes.
                            </p>
                            <p className="text-xs text-muted-foreground text-center">
                                Didn&rsquo;t receive the email? Check your spam folder, or try again
                                after a minute.
                            </p>
                            <div className="flex justify-center pt-2">
                                <Link
                                    to="/login"
                                    className="text-sm text-primary font-medium inline-flex items-center gap-1.5"
                                    data-testid="forgot-password-back-to-login"
                                >
                                    <ArrowLeft className="h-4 w-4" /> Back to sign in
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-5">
                            <p className="text-sm text-muted-foreground">
                                Enter the email address on your account and we&rsquo;ll send you a
                                one-time password reset link.
                            </p>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    autoFocus
                                    data-testid="forgot-password-email-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-primary hover:bg-primary/90 gap-1.5"
                                disabled={submitting || !email}
                                data-testid="forgot-password-submit-button"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Sending…
                                    </>
                                ) : (
                                    "Send reset link"
                                )}
                            </Button>
                            <div className="text-center">
                                <Link
                                    to="/login"
                                    className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
                                    data-testid="forgot-password-back-link"
                                >
                                    <ArrowLeft className="h-4 w-4" /> Back to sign in
                                </Link>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
